// Gateway mode: a hidden panel in the Mac app that is a session participant like everyone else, but its
// "headset" is a line input / output of an audio interface (4-wire intercom, Riedel, Green-GO …).
// Mic: one channel of the input device (no voice processing) with VOX or always-open instead of a talk key.
// Speaker: the mix of what this role listens to, on one channel of the output device.
// Everything else (path choice, failover, jitter buffer, mixing, Opus) is the normal SessionClient.
import { SessionClient } from './session.js';

const gw = window.smolaGw; // preload of the hidden window: config() / status() / devices()

const dbToGain = (db) => 10 ** ((Number(db) || 0) / 20);
const peakDb = (an, buf) => {
  an.getFloatTimeDomainData(buf);
  let peak = 0;
  for (const v of buf) peak = Math.max(peak, Math.abs(v));
  return 20 * Math.log10(peak + 1e-9);
};

/** list inputs/outputs with their real channel count (opens each input once) */
async function probeDevices(want = {}) {
  let devs = await navigator.mediaDevices.enumerateDevices();
  if (devs.some((d) => d.kind !== 'videoinput' && !d.label)) {
    // names are only visible after one permission grant – open the default input once, for that
    const s = await navigator.mediaDevices.getUserMedia({ audio: true }).catch((e) => ({ error: e.message }));
    if (s.error) return { error: s.error, inputs: [], outputs: [] };
    s.getTracks().forEach((t) => t.stop());
    devs = await navigator.mediaDevices.enumerateDevices();
  }
  const inputs = [];
  for (const d of devs.filter((x) => x.kind === 'audioinput' && x.deviceId !== 'default' && x.deviceId !== 'communications')) {
    let channels = 0; // 0 = not looked at yet: a device is only opened once it is chosen
    if (d.label === want.input) try {
      const t = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: d.deviceId }, channelCount: { ideal: 64 }, echoCancellation: false, noiseSuppression: false, autoGainControl: false } });
      channels = t.getAudioTracks()[0].getSettings().channelCount || 0;
      t.getTracks().forEach((x) => x.stop());
    } catch {}
    inputs.push({ name: d.label, channels });
  }
  const ctx = new AudioContext();
  const outputs = [];
  for (const d of devs.filter((x) => x.kind === 'audiooutput' && x.deviceId !== 'default' && x.deviceId !== 'communications')) {
    let channels = 0;
    if (d.label === want.output) try {
      await ctx.setSinkId(d.deviceId);
      channels = ctx.destination.maxChannelCount;
    } catch { channels = 2; }
    outputs.push({ name: d.label, channels });
  }
  ctx.close();
  return { inputs, outputs };
}

async function deviceByLabel(kind, label) {
  const devs = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === kind);
  return devs.find((d) => d.label === label)?.deviceId || null;
}

async function run(id) {
  const cfg = await gw.config(id);
  try {
    localStorage.setItem('talk.noWarm', '1'); // no standby connection to the other engine: it showed up as a second entry of the port
  } catch {}
  const st = { state: 'connecting', error: '', inDb: -90, outDb: -90, talking: false, path: '', inChannels: 0, outChannels: 0 };
  const push = () => gw.status(id, st);
  setInterval(push, 100); // the levels in the host UI move with it
  if (!cfg) return void ((st.state = 'error'), (st.error = 'no configuration'), push());

  const client = new SessionClient({ token: cfg.token, sessionId: cfg.sessionId });
  window.__talk = client;
  window.__gw = st;
  client.noGuard = cfg.inDevice === 'test:sine'; // a test tone is a steady tone by design
  // incoming call signal → the host closes the CALL contact; contact/webhook → call signal into the session
  client.on('signal', (m) => ((st.callSeq = (st.callSeq || 0) + 1), (st.callAt = Date.now()), (st.callFrom = m.from?.name || '')));
  gw.onCommand?.((cmd) => {
    if (cmd?.op !== 'call' || !client.me) return;
    const ch = cfg.callChannel || (cfg.talk?.length ? cfg.talk[0] : client.me.canTalk?.[0]);
    if (ch) client.signal(`ch:${ch}`).catch(() => {});
  });
  client.on('feedback', (f) => ((st.feedback = f.until), (st.feedbackHz = f.hz)));
  const ctx = (client.ctx = new AudioContext({ latencyHint: 'interactive' }));

  // ---- input: one channel of the device (or a test tone) → gain → delay (pre-roll for VOX) → mono stream
  const inGain = ctx.createGain();
  inGain.gain.value = dbToGain(cfg.inGainDb);
  let inSrc;
  const rtp = cfg.type === 'rtp';
  if (rtp) {
    // network port: the app receives the RTP stream and hands the samples over (see app/src/rtp-bridge.js)
    await ctx.audioWorklet.addModule('pcm-worklet.js');
    const node = new AudioWorkletNode(ctx, 'ot-pcm-in', { numberOfInputs: 0, outputChannelCount: [1] });
    gw.onPcm?.((u8) => {
      st.pcmFrames = (st.pcmFrames || 0) + 1;
      const i16 = new Int16Array(u8.buffer, u8.byteOffset, u8.byteLength >> 1);
      const f = new Float32Array(i16.length);
      let pk = 0;
      for (let i = 0; i < i16.length; i++) ((f[i] = i16[i] / 32768), (pk = Math.max(pk, Math.abs(f[i]))));
      st.pcmPeak = Math.max(pk, (st.pcmPeak || 0) * 0.9);
      node.port.postMessage(f, [f.buffer]);
    });
    inSrc = node;
    st.inChannels = 1;
  } else if (!cfg.inDevice) {
    inSrc = ctx.createGain(); // “none”: no input is opened (the default one could be an iPhone microphone)
    st.inChannels = 0;
  } else if (cfg.inDevice === 'test:sine') {
    const o = ctx.createOscillator();
    o.frequency.value = 660;
    const g = ctx.createGain();
    g.gain.value = 0.2;
    o.connect(g);
    o.start();
    inSrc = g;
    st.inChannels = 1;
  } else {
    const deviceId = cfg.inDevice ? await deviceByLabel('audioinput', cfg.inDevice) : null;
    if (cfg.inDevice && !deviceId) {
      st.state = 'error';
      st.error = `Input “${cfg.inDevice}” not found`;
      return push();
    }
    const want = Math.max(2, Number(cfg.inChannel) || 1);
    const stream = await navigator.mediaDevices
      .getUserMedia({ audio: { ...(deviceId ? { deviceId: { exact: deviceId } } : {}), channelCount: { ideal: want }, echoCancellation: false, noiseSuppression: false, autoGainControl: false } })
      .catch((e) => ((st.state = 'error'), (st.error = `Input: ${e.message}`), null));
    if (!stream) return push();
    st.inChannels = stream.getAudioTracks()[0].getSettings().channelCount || 1;
    const k = Math.min(Math.max(1, Number(cfg.inChannel) || 1), st.inChannels) - 1;
    const src = ctx.createMediaStreamSource(stream);
    const split = ctx.createChannelSplitter(Math.max(1, st.inChannels));
    src.connect(split);
    const pick = ctx.createGain();
    split.connect(pick, k, 0);
    inSrc = pick;
  }
  inSrc.connect(inGain);
  const meter = ctx.createAnalyser();
  meter.fftSize = 1024;
  inGain.connect(meter);
  const preroll = ctx.createDelay(0.5);
  preroll.delayTime.value = 0.06; // the VOX decision is 60 ms ahead of the audio → first syllable is not cut
  inGain.connect(preroll);
  const dest = ctx.createMediaStreamDestination();
  preroll.connect(dest);

  await client.start({ micStream: dest.stream, outputDeviceId: null }).catch((e) => ((st.state = 'error'), (st.error = e.message)));
  if (st.state === 'error') return push();

  // ---- output: channel k of the device (stereo/multichannel), or discard; network port: samples to the app
  const outDevice = rtp || !cfg.outDevice ? 'discard' : cfg.outDevice;
  if (outDevice === 'discard') {
    client.master.disconnect();
    st.outChannels = 0;
  } else if (outDevice && outDevice !== 'default') {
    const deviceId = await deviceByLabel('audiooutput', outDevice);
    if (!deviceId) (st.state = 'error'), (st.error = `Output “${outDevice}” not found`);
    else await ctx.setSinkId(deviceId).catch((e) => ((st.state = 'error'), (st.error = `Output: ${e.message}`)));
  }
  if (outDevice !== 'discard' && st.state !== 'error') {
    const k = Math.max(1, Number(cfg.outChannel) || 1);
    const n = Math.min(ctx.destination.maxChannelCount, Math.max(k, 2));
    st.outChannels = ctx.destination.maxChannelCount;
    if (k > ctx.destination.maxChannelCount) {
      st.state = 'error';
      st.error = `Output channel ${k}, but the device has only ${ctx.destination.maxChannelCount}`;
    } else if (n > 2 || k > 1 || cfg.outMono !== false) {
      ctx.destination.channelCount = n;
      ctx.destination.channelInterpretation = 'discrete';
      const mono = ctx.createGain();
      mono.channelCount = 1;
      mono.channelCountMode = 'explicit';
      mono.gain.value = dbToGain(cfg.outGainDb);
      const merge = ctx.createChannelMerger(n);
      client.master.disconnect();
      client.master.connect(mono);
      mono.connect(merge, 0, k - 1);
      merge.connect(ctx.destination);
      client.outTap = mono;
    }
  }
  if (rtp) {
    const o = new AudioWorkletNode(ctx, 'ot-pcm-out', { numberOfOutputs: 1, channelCount: 1, channelCountMode: 'explicit' });
    const g = ctx.createGain();
    g.gain.value = dbToGain(cfg.outGainDb);
    client.master.connect(g);
    g.connect(o);
    const sink = ctx.createGain();
    sink.gain.value = 0; // the worklet only runs while it is connected to the output
    o.connect(sink).connect(ctx.destination);
    o.port.onmessage = (e) => {
      const f = e.data;
      const i16 = new Int16Array(f.length);
      for (let i = 0; i < f.length; i++) i16[i] = Math.max(-32768, Math.min(32767, Math.round(f[i] * 32767)));
      gw.pcmOut?.(id, i16.buffer);
    };
    client.outTap = g;
    st.outChannels = 1;
  }
  const outMeter = ctx.createAnalyser();
  outMeter.fftSize = 1024;
  (client.outTap || client.master).connect(outMeter);

  // ---- VOX / always / off → talk keys of the configured channels
  // talk keys: the ticked channels, none ticked = every channel the role may talk on
  const targets = () => {
    const can = client.me?.canTalk || [];
    return (cfg.talk?.length ? cfg.talk.filter((c) => can.includes(c)) : can).map((c) => `ch:${c}`);
  };
  let sent = [];
  let open = cfg.mode === 'always';
  let holdUntil = 0;
  const setOpen = (on) => {
    if (on === open && st.talking === on) return;
    open = on;
    st.talking = on;
    const now = on ? targets() : [];
    for (const t of sent) if (!now.includes(t)) client.setTalk(t, false);
    for (const t of now) client.setTalk(t, true);
    sent = now;
  };
  const inBuf = new Float32Array(meter.fftSize);
  const outBuf = new Float32Array(outMeter.fftSize);
  const joined = () => client.status.media === 'connected' && client.me;
  setInterval(() => {
    st.inDb = peakDb(meter, inBuf);
    st.outDb = peakDb(outMeter, outBuf);
    st.path = client.path || '';
    st.detail = JSON.stringify(client.status);
    if (st.feedback && st.feedback < Date.now()) st.feedback = 0;
    st.state = st.state === 'error' ? 'error' : joined() ? 'ready' : 'connecting';
    if (!joined()) return;
    if (cfg.mode === 'off') return setOpen(false);
    if (cfg.mode === 'always') return setOpen(true);
    const now = performance.now();
    if (st.inDb > cfg.voxDb) holdUntil = now + (cfg.holdMs || 600);
    setOpen(now < holdUntil);
  }, 20);
  client.on('fatal', (m) => ((st.state = 'error'), (st.error = String(m))));
  client.on('error', (m) => (st.error = String(m)));
  // keys sent before joining are lost – send them again after (re)joining
  client.on('me', () => ((sent = []), st.talking && setOpen(true)));
}

export function startGateway(id) {
  document.title = `Gateway ${id}`;
  if (id === 'probe') {
    const q = new URLSearchParams(location.search);
    probeDevices({ input: q.get('in') || '', output: q.get('out') || '' }).then((r) => gw.devices(r));
    return;
  }
  run(id).catch((e) => gw.status(id, { state: 'error', error: e.message, inDb: -90, outDb: -90 }));
}
